import { BreakPointCondition, UI, useBreakpointIf } from '@genial/components';
import { MetaResponse } from '@genial/services/dist/models';
import { Box } from 'native-base';
import React, { useEffect, useState } from 'react';
import { Dimensions, DimensionValue, FlatList } from 'react-native';
import { RecoilValueReadOnly, useRecoilState, useRecoilValue } from 'recoil';

import { FilterModalTypes } from '../../../../../components/FilterModal';
import { getEmptySelections } from '../../../../../components/FilterModal/constants';
import {
  TableHeadContainer,
  TableHeadItem,
  TableHeadItemRecurse,
} from '../../../../../components/TableHead';
import { FiltersToApplyProps } from '../../../../../hooks/useFilterTransformer';
import { useRecoilQuery } from '../../../../../hooks/useRecoilQuery';
import { selectedInvestmentResumeYearState } from '../../../../../store/managementAnalysis/atom';
import { managementAnalysisFiltersAtom } from '../../../../../store/managementAnalysisDetails/filters';
import { emptyCategoriesQuery } from '../../../../../store/managementAnalysisDetails/selector';
import { AbstractProductsQueryData } from '../../../../../store/managementAnalysisDetails/types';
import { ProductSearchError } from '../../ProductSearchError';
import { ProductTableMobile, ProductTableMobileProps } from '../ProductTableMobile';
import { ProductTableTablet } from '../ProductTableTablet';

import { LoadingTable, NotFoundTable, ProductTableProps, getYearOrDefault } from './constants';

type ProductsQueryParams = {
  year: number;
  page: number;
  searchTerm?: string;
  filtersToApply: FiltersToApplyProps;
};

type ProductsQueryResult<T> = RecoilValueReadOnly<
  | {
      meta: MetaResponse;
      data: AbstractProductsQueryData<T>;
    }
  | undefined
>;

type ProductCategoryQueryResult = RecoilValueReadOnly<
  | {
      data: FilterModalTypes.FilterType[];
    }
  | undefined
>;

const ItemSeparator = () => <Box height="stack.nano" />;

export interface DynamicProductTableProps<T> extends ProductTableProps {
  tableHeadItems: TableHeadItemRecurse[];
  mobileFields: ProductTableMobileProps<T>['fields'];
  tabletColumns: number;
  productsQuery: (param: ProductsQueryParams) => ProductsQueryResult<T>;
  productCategoryQuery?: (param: { year: number }) => ProductCategoryQueryResult;
  productsFiltersGetter: (
    categories: FilterModalTypes.FilterType[]
  ) => FilterModalTypes.FilterType[];
  ProductItem: React.FC<{ item: T; product: ProductTableProps['product'] }>;
  SecondaryInfo?: React.FC<{ item: T }>;
}

export const DynamicProductTable = <T,>({
  setDataLength,
  setIsLoading,
  product,
  searchTerm,
  filtersToApply,
  productsQuery,
  productCategoryQuery,
  productsFiltersGetter,
  mobileFields,
  tableHeadItems,
  ProductItem,
  tabletColumns,
  SecondaryInfo,
}: DynamicProductTableProps<T>) => {
  const columns = tableHeadItems.length;
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');

  const [page, setPage] = useState(1);
  const [allData, setAllData] = useState<T[]>([]);
  const maxHeight = `${Dimensions.get('window').height - 295}px` as DimensionValue;

  const yearSelected = useRecoilValue(selectedInvestmentResumeYearState);
  const year = getYearOrDefault(yearSelected);

  const [_, setFiltersState] = useRecoilState(managementAnalysisFiltersAtom);

  const { loading, data, error, refresher } = useRecoilQuery(
    productsQuery({ page, searchTerm, filtersToApply, year })
  );

  const { data: categories } = useRecoilQuery(
    productCategoryQuery ? productCategoryQuery({ year }) : emptyCategoriesQuery(undefined)
  );

  const renderTableHead = () => (
    <TableHeadContainer px="inline.xxxs" display="flex">
      {tableHeadItems.map((item, index) => (
        <TableHeadItem key={`${item.title}-${index}`} {...item} value={item.orderValue} />
      ))}
    </TableHeadContainer>
  );

  useEffect(() => {
    const newFilters = productsFiltersGetter(categories?.data || []);
    setFiltersState((prev) => ({
      ...prev,
      availableFilters: newFilters,
      selectedFilters: getEmptySelections(newFilters),
      appliedFilters: [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  useEffect(() => {
    setAllData([]);
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (!Object.keys(filtersToApply).length) {
      return;
    }
    setAllData([]);
    setPage(1);
  }, [filtersToApply]);

  useEffect(() => {
    if (error) {
      setAllData([]);
      setPage(1);
    }
  }, [error]);

  useEffect(() => {
    setDataLength(allData.length);
  }, [allData, setDataLength]);

  useEffect(() => {
    const items = data?.data;

    if (!loading && items?.length) {
      setAllData((prev) => [...prev, ...items]);
    }

    setIsLoading(loading);
  }, [loading, data, setAllData, setIsLoading]);

  const handleEndReached = () => {
    if (loading || data?.meta.pagination.page !== page || error) {
      return;
    }
    if (data?.meta.pagination.hasNext) {
      setPage((prev) => prev + 1);
    }
  };

  const showLoading = loading && !(allData.length && page > 1);

  if (isMobile) {
    return (
      <ProductTableMobile
        data={allData}
        isLoading={showLoading}
        isLoadingMore={loading}
        handleEndReached={handleEndReached}
        fields={mobileFields}
        ItemBottomComponent={SecondaryInfo}
      />
    );
  }

  if (isTablet) {
    return (
      <ProductTableTablet
        data={allData}
        columns={tabletColumns}
        isLoading={showLoading}
        isLoadingMore={loading}
        handleEndReached={handleEndReached}
        fields={mobileFields}
        product={product}
        SecondaryInfo={SecondaryInfo}
      />
    );
  }

  if (showLoading) {
    return (
      <Box overflow="hidden">
        <UI.Table isLoading={false} cols={columns} rows={5}>
          {renderTableHead()}
          <LoadingTable columnsItems={tableHeadItems} />;
        </UI.Table>
      </Box>
    );
  }

  if (error) {
    return <ProductSearchError handleReload={refresher} />;
  }

  if (allData?.length === 0 && !loading) {
    return <NotFoundTable />;
  }

  const loadingFooter = () => {
    return <Box>{!!loading && <LoadingTable items={1} columnsItems={tableHeadItems} />}</Box>;
  };

  return (
    <Box overflow="hidden">
      <UI.Table isLoading={showLoading} cols={columns} rows={5}>
        {renderTableHead()}

        <FlatList
          windowSize={20}
          maxToRenderPerBatch={20}
          updateCellsBatchingPeriod={5}
          data={allData}
          contentContainerStyle={{ maxHeight }}
          ListEmptyComponent={<LoadingTable columnsItems={tableHeadItems} />}
          showsVerticalScrollIndicator={false}
          onEndReached={handleEndReached}
          onEndReachedThreshold={1.5}
          ItemSeparatorComponent={ItemSeparator}
          ListFooterComponent={loadingFooter}
          renderItem={({ item }) => <ProductItem product={product} item={item} />}
        />
      </UI.Table>
    </Box>
  );
};
